import React, { FC } from 'react'
import InfoBlock from './InfoBlock'
import { Option } from '@mortgage-pos/types'
import Checkmark from '@mortgage-pos/ui/assets/Checkmark.svg'
import BankrateCheckmark from '@mortgage-pos/ui/assets/bankrate/BankrateCheckmark.svg'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'

export interface Props {
  name: String
  title: string
  subTitle?: string
  value: string
  error: string
  touched: boolean
  options: Option[]
  modifierClass: string
  infoContent?: string
  setValue: (value: string) => void
}

export const MultiSelectButton: FC<Props> = ({
  name,
  title,
  subTitle,
  value,
  error,
  options,
  touched,
  modifierClass,
  infoContent,
  setValue,
}) => {
  const bankrateCompareEnabled = useVariation(
    FEATURE_FLAG_TYPES.BANKRATE_COMPARE_ENABLED
  )
  const handleClick = (value: string) => (): void => {
    setValue(value)
  }

  const handleKeyPress =
    (value: string) =>
    (event): void => {
      if (['Enter', ' '].includes(event.key)) {
        setValue(value)
      }
    }

  return (
    <div
      className={`multi-select-button__wrapper ${modifierClass || ''} ${
        error && touched ? '--error' : ''
      }`}
      data-testid={`multi-select-button__wrapper-${name}`}
    >
      <div className="multi-select-button__title">{title}</div>
      {subTitle ? (
        <div className="multi-select-button__subtitle">{subTitle}</div>
      ) : null}

      <div className="multi-select-button__options-wrapper">
        {options.map((option) => {
          const buttonSelectedClass =
            option.value === value?.toString() ? '--selected' : ''
          return (
            <div
              key={option.text}
              className="multi-select-button__option-container"
            >
              <div
                data-testid={`multi-select-button__option:${option.value}`}
                className={`multi-select-button__option ${buttonSelectedClass}`}
                onClick={handleClick(option.value)}
                onKeyUp={handleKeyPress(option.value)}
                tabIndex={0}
              >
                <div className="multi-select-button__option-details">
                  {option.iconName && !(modifierClass === '--half-width') ? (
                    <img
                      data-testid={`multi-select-button__option-icon:${option.iconName}`}
                      className="multi-select-button__option-icon"
                      src={`../../../assets/${option.iconName}.svg`}
                      alt="icon"
                    />
                  ) : null}
                  {option.iconName && !(modifierClass === '--half-width') ? (
                    <span
                      data-testid={`multi-select-button__option-text-icon:${option.value}`}
                      className={`multi-select-button__option-text-icon ${buttonSelectedClass}`}
                    >
                      {option.text}
                    </span>
                  ) : (
                    <span
                      data-testid={`multi-select-button__option-text:${option.value}`}
                      className={`multi-select-button__option-text ${buttonSelectedClass}`}
                    >
                      {option.text}
                    </span>
                  )}
                </div>
                <img
                  className={`multi-select-button__option-text-checkmark ${buttonSelectedClass}`}
                  src={bankrateCompareEnabled ? BankrateCheckmark : Checkmark}
                  alt="checkmark icon"
                />
              </div>
            </div>
          )
        })}
      </div>

      {error && touched ? <InfoBlock type="danger">{error}</InfoBlock> : null}
      {infoContent ? (
        <div data-testid={'multi-select-button__info-block'}>
          <InfoBlock>{infoContent}</InfoBlock>
        </div>
      ) : null}
    </div>
  )
}

export default MultiSelectButton
